import type { SupWeb3Address } from "@/types";
import React, { useCallback, useMemo } from "react";

import UiLabelChainScanLink from "@/components/Ui/Label/ChainScanLink";
import Web3CommonSwitchNetwork from "@/components/Web3/Common/SwitchNetwork";
import MigrateOrganismTxTransact from "@/features/migrate/ui/Organism/Tx/Transact";
import MigrateOrganismTxApproval from "@/features/migrate/ui/Organism/Tx/Approval/Wrapper";

import { SupAssetLabel, SupButton, SupText, SupCheckCircle, SupSuperformMorph } from "@repo/core-ui";

import { useDynamicContext } from "@dynamic-labs/sdk-react-core";

import { useMigrateStore } from "@/features/migrate/state/StoreProvider";
import useMigrateStart from "@/features/migrate/api/useMigrateStart";
import useMigrateUserVaultShares from "@/features/migrate/api/useMigrateUserVaultShares";
import useChainInfo from "@/hooks/useChainInfo";

import Image from "next/image";
import { twMerge } from "tailwind-merge";
import { getProtocolImage, getProtocolName } from "@/utils";
import { useStoredDataDispatch } from "@/store";

interface MigratePageWeb3WrapperProps {
  className?: string;
  headerClassName?: string;
}

export default function MigratePageWeb3Wrapper({ headerClassName, className }: MigratePageWeb3WrapperProps) {
  const { network } = useDynamicContext();
  const storedDispatch = useStoredDataDispatch();

  const { vaultShares, txData, setTxData, reset, completeApprovals } = useMigrateStore(store => store);
  const { mutate, isLoading } = useMigrateUserVaultShares();
  const { data: apiData, error: apiError, isLoading: apiIsLoading } = useMigrateStart(vaultShares);

  const requireApproval = useMemo(() => (apiData ? (apiData.approvals?.length ?? 0) > completeApprovals.length : false), [apiData, completeApprovals.length]);

  const chainId = useMemo<undefined | number>(() => vaultShares?.[0]?.vault?.chain?.id ?? undefined, [vaultShares]);
  const { data: chainInfo } = useChainInfo(chainId);

  const requireSwitchChain = useMemo(() => {
    return network && chainId ? parseInt(`${network}`, 10) !== chainId : false;
  }, [chainId, network]);

  const handleTxHash = useCallback(
    (hash: string) => {
      setTxData(hash as SupWeb3Address);
    },
    [setTxData]
  );

  const sharesGroupedByProtocol = useMemo(() => {
    return vaultShares.reduce<Record<string, typeof vaultShares>>((acc, item) => {
      const protocol = getProtocolName(item.vault);
      if (!acc[protocol]) {
        acc[protocol] = [];
      }
      acc[protocol].push(item);
      return acc;
    }, {});
  }, [vaultShares]);

  const handleDone = useCallback(() => {
    mutate()
      .then(() => {
        reset();
        storedDispatch({ type: "closeOverlay" });
      })
      .catch(() => {
        reset();
      });
  }, [mutate, reset, storedDispatch]);

  return (
    <div className={twMerge("relative flex h-full max-h-screen flex-col justify-start overflow-y-scroll p-6 pt-[60px]", className)}>
      <header className={twMerge("border-bottom-[.5px] fixed top-0 z-50 flex w-[370px] items-center justify-between space-x-2 border-neutral-500 py-2.5", headerClassName)}>
        <h2 className="txt-header-dgt my-0 text-[16px] uppercase leading-10 text-neutral-900">Migrate</h2>
        <SupSuperformMorph className="h-6 w-6 fill-neutral-900" />
      </header>
      <div className="space-y-2">
        <div className="flex h-10 w-full items-center justify-between border-b-[.5px] border-neutral-200">
          {!!chainInfo && (
            <SupAssetLabel
              className="ml-[1px] flex items-center gap-2 text-neutral-900"
              label={
                <SupText variant="caption" weight="bold" className="text-[14px] leading-4">
                  {chainInfo?.name}
                </SupText>
              }
            >
              <Image unoptimized={true} width={14} height={14} src={chainInfo?.icon} alt={chainInfo?.name} />
            </SupAssetLabel>
          )}
          {txData && (
            <SupText variant="caption" as="div" className="inline-flex items-center gap-2 text-[12px] font-normal leading-4 tracking-[1px] text-success-800">
              <SupCheckCircle />
              <span>Migrated</span>
            </SupText>
          )}
        </div>
        {Object.entries(sharesGroupedByProtocol).map(([protocolName, shares], index) => (
          <div className="inline-flex h-12 w-full items-center justify-between gap-4 text-neutral-925" key={`migrate-protocol-shares-${index}`}>
            <div className="inline-flex h-4 w-fit items-center justify-start gap-1">
              <div className="flex h-4 w-4 items-center justify-center overflow-hidden rounded-full">
                <img className="shrink grow basis-0 self-stretch" src={getProtocolImage(shares[0].vault)} width={16} height={16} alt={protocolName} />
              </div>
              <SupText variant="subtitle1" className="text-[14px] leading-4 tracking-[1px]">
                {protocolName}
              </SupText>
            </div>
            <SupText variant="subtitle1" className="text-[14px] leading-3 tracking-[1px]">
              {shares.length}&nbsp;{`position${shares.length > 1 ? "s" : ""}`}
            </SupText>
          </div>
        ))}
      </div>
      {txData && chainId && (
        <div className="-ml-2 mt-6">
          <UiLabelChainScanLink urlLabel="Explorer" prefixLabel="View on" address={txData} chain={chainId} />
        </div>
      )}

      <div className="mt-auto flex w-full flex-col items-stretch justify-between space-y-4">
        {txData ? (
          <SupButton fluid onClick={handleDone} disabled={isLoading} className="bg-primary-800 hover:opacity-80" color="primary">
            Done
          </SupButton>
        ) : requireSwitchChain ? (
          <Web3CommonSwitchNetwork targetChainId={parseInt(`${chainId}`)} />
        ) : (
          chainId &&
          (requireApproval ? (
            <MigrateOrganismTxApproval approvals={apiData?.approvals} chainId={chainId} />
          ) : (
            <MigrateOrganismTxTransact apiData={apiData} apiError={apiError} apiIsLoading={apiIsLoading} handleTxHash={handleTxHash} chainId={chainId} />
          ))
        )}
      </div>
    </div>
  );
}
